import { useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { CruiseReservation, ItemPricing, useUpdateTripItem } from "@/hooks/useTrips";
import { toast } from "sonner";
import { PriceInputSection } from "../PriceInputSection";
import { Plus, Trash2 } from "lucide-react";

const cruiseSchema = z.object({
  cruise_line: z.string().min(1, "Cruise line is required"),
  ship_name: z.string().min(1, "Ship name is required"),
  cruise_title: z.string().optional(),
  // Departure
  departure_port: z.string().min(1, "Departure port is required"),
  departure_date: z.string().min(1, "Departure date is required"),
  departure_time: z.string().optional(),
  // Arrival
  arrival_port: z.string().optional(),
  arrival_date: z.string().optional(),
  arrival_time: z.string().optional(),
  // Cabin
  cabin_number: z.string().optional(),
  cabin_type: z.string().optional(),
  deck: z.string().optional(),
  confirmation_number: z.string().optional(),
});

type CruiseFormData = z.infer<typeof cruiseSchema>;

interface EditCruiseDialogProps {
  cruise: CruiseReservation;
  tripId: string;
  index: number;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export const EditCruiseDialog = ({
  cruise,
  tripId,
  index,
  open,
  onOpenChange,
}: EditCruiseDialogProps) => {
  const updateItem = useUpdateTripItem();
  const [pricing, setPricing] = useState<ItemPricing | undefined>(cruise.pricing);
  const [passengers, setPassengers] = useState<{ name: string }[]>(cruise.passengers || []);

  const form = useForm<CruiseFormData>({
    resolver: zodResolver(cruiseSchema),
    defaultValues: {
      cruise_line: cruise.cruise_line || "",
      ship_name: cruise.ship_name || "",
      cruise_title: cruise.cruise_title || "",
      departure_port: cruise.departure_port || "",
      departure_date: cruise.departure_date || "",
      departure_time: cruise.departure_time || "",
      arrival_port: cruise.arrival_port || "",
      arrival_date: cruise.arrival_date || "",
      arrival_time: cruise.arrival_time || "",
      cabin_number: cruise.cabin_number || "",
      cabin_type: cruise.cabin_type || "",
      deck: cruise.deck || "",
      confirmation_number: cruise.confirmation_number || "",
    },
  });

  const addPassenger = () => {
    setPassengers([...passengers, { name: "" }]);
  };

  const removePassenger = (idx: number) => {
    setPassengers(passengers.filter((_, i) => i !== idx));
  };

  const updatePassenger = (idx: number, value: string) => {
    const updated = [...passengers];
    updated[idx] = { name: value };
    setPassengers(updated);
  };

  const onSubmit = (data: CruiseFormData) => {
    // Filter out empty passengers
    const validPassengers = passengers.filter(p => p.name.trim());

    // Preserve enrichment data when editing
    const updatedCruise: CruiseReservation = {
      ...cruise,
      ...data,
      pricing,
      passengers: validPassengers,
    };

    updateItem.mutate(
      {
        tripId,
        itemType: "cruises",
        index,
        item: updatedCruise,
      },
      {
        onSuccess: () => {
          toast.success("Cruise updated");
          onOpenChange(false);
        },
        onError: (error) => {
          toast.error(`Failed to update: ${error.message}`);
        },
      }
    );
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="sm:max-w-lg overflow-y-auto">
        <SheetHeader>
          <SheetTitle>Edit Cruise</SheetTitle>
        </SheetHeader>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6 mt-6">
            {/* Cruise Info Section */}
            <div className="space-y-4">
              <h3 className="text-sm font-medium text-muted-foreground">Cruise Information</h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <FormField
                  control={form.control}
                  name="cruise_line"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Cruise Line</FormLabel>
                      <FormControl>
                        <Input placeholder="Royal Caribbean" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="ship_name"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Ship Name</FormLabel>
                      <FormControl>
                        <Input placeholder="Symphony of the Seas" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
              <FormField
                control={form.control}
                name="cruise_title"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Cruise Title</FormLabel>
                    <FormControl>
                      <Input placeholder="7-Night Caribbean Cruise" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="confirmation_number"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Confirmation #</FormLabel>
                    <FormControl>
                      <Input placeholder="ABC123" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <Separator />

            {/* Departure Section */}
            <div className="space-y-4">
              <h3 className="text-sm font-medium text-muted-foreground">Departure</h3>
              <FormField
                control={form.control}
                name="departure_port"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Port</FormLabel>
                    <FormControl>
                      <Input placeholder="Miami, FL" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <FormField
                  control={form.control}
                  name="departure_date"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Date</FormLabel>
                      <FormControl>
                        <Input type="date" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="departure_time"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Time</FormLabel>
                      <FormControl>
                        <Input type="time" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
            </div>

            <Separator />

            {/* Arrival Section */}
            <div className="space-y-4">
              <h3 className="text-sm font-medium text-muted-foreground">Return</h3>
              <FormField
                control={form.control}
                name="arrival_port"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Port</FormLabel>
                    <FormControl>
                      <Input placeholder="Miami, FL" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <FormField
                  control={form.control}
                  name="arrival_date"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Date</FormLabel>
                      <FormControl>
                        <Input type="date" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="arrival_time"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Time</FormLabel>
                      <FormControl>
                        <Input type="time" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
            </div>

            <Separator />

            {/* Cabin Section */}
            <div className="space-y-4">
              <h3 className="text-sm font-medium text-muted-foreground">Cabin Details</h3>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <FormField
                  control={form.control}
                  name="cabin_number"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Cabin #</FormLabel>
                      <FormControl>
                        <Input placeholder="8210" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="cabin_type"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Type</FormLabel>
                      <FormControl>
                        <Input placeholder="Balcony" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="deck"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Deck</FormLabel>
                      <FormControl>
                        <Input placeholder="8" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
            </div>

            <Separator />

            {/* Passengers Section */}
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <h3 className="text-sm font-medium text-muted-foreground">Passengers</h3>
                <Button type="button" variant="outline" size="sm" onClick={addPassenger}>
                  <Plus className="h-3 w-3 mr-1" />
                  Add
                </Button>
              </div>
              {passengers.length === 0 ? (
                <p className="text-sm text-muted-foreground">No passengers added</p>
              ) : (
                <div className="space-y-2">
                  {passengers.map((passenger, idx) => (
                    <div key={idx} className="flex gap-2 items-center">
                      <Input
                        placeholder="Passenger Name"
                        value={passenger.name}
                        onChange={(e) => updatePassenger(idx, e.target.value)}
                        className="flex-1"
                      />
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        className="h-9 w-9 text-muted-foreground hover:text-destructive"
                        onClick={() => removePassenger(idx)}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <Separator />

            {/* Pricing Section */}
            <PriceInputSection
              pricing={pricing}
              onChange={setPricing}
            />

            <div className="flex justify-end gap-2 pt-4">
              <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={updateItem.isPending}>
                {updateItem.isPending ? "Saving..." : "Save Changes"}
              </Button>
            </div>
          </form>
        </Form>
      </SheetContent>
    </Sheet>
  );
};
